/**
 * Work Order Operation Search Utilities
 *
 * Generic utilities for parsing and handling multi-field work order operation searches.
 * These utilities are shared across barcode scanning and manual search functionality.
 */

import {
  DEFAULT_FORMATS,
  validateJobDisplayNameFormat,
} from 'lib/utils/jobDisplayName';

// Work order operation field names for multi-field search
export const WORK_ORDER_OPERATION_FIELDS = {
  WORK_ORDER_ID: 'workOrderId',
  SEQUENCE_NUMBER: 'sequenceNumber',
  LOT: 'lot',
  SUB: 'sub',
  SPLIT: 'split',
};

const FIELD_DELIMITER = ' | ';
const FIELD_SEPARATOR = ': ';

/**
 * Parse a search string into individual work order operation fields
 *
 * @param {string} searchString - Search string with format "workOrderId: 123 | sequenceNumber: 10"
 * @returns {Object} Object with field names as keys and values
 *
 * @example
 * parseSearchFields("workOrderId: 123 | sequenceNumber: 10")
 * // Returns: { workOrderId: "123", sequenceNumber: "10" }
 */
export const parseSearchFields = (searchString) => {
  if (!searchString || typeof searchString !== 'string') {
    return {};
  }

  const fields = {};
  const parts = searchString.split(FIELD_DELIMITER).filter((part) => {
    return part.trim();
  });

  parts.forEach((part) => {
    const trimmedPart = part.trim();
    const separatorIndex = trimmedPart.indexOf(FIELD_SEPARATOR);

    if (separatorIndex > -1) {
      const fieldName = trimmedPart.substring(0, separatorIndex).trim();
      const value = trimmedPart
        .substring(separatorIndex + FIELD_SEPARATOR.length)
        .trim();

      if (
        fieldName &&
        value &&
        Object.values(WORK_ORDER_OPERATION_FIELDS).includes(fieldName)
      ) {
        fields[fieldName] = value;
      }
    } else {
      // Fallback: If no separator, assume it's workOrderId for backwards compatibility
      fields[WORK_ORDER_OPERATION_FIELDS.WORK_ORDER_ID] = trimmedPart;
    }
  });

  return fields;
};

/**
 * Check if search string contains barcode field prefixes
 * Barcode format: "fieldName: value | fieldName: value"
 *
 * @param {string} searchString - Search string to check
 * @returns {boolean} True if contains field prefixes (barcode format)
 *
 * @example
 * isBarcodeFormattedSearch("workOrderId: 123")
 * // Returns: true
 *
 * @example
 * isBarcodeFormattedSearch("12345")
 * // Returns: false
 */
export const isBarcodeFormattedSearch = (searchString) => {
  if (!searchString || typeof searchString !== 'string') {
    return false;
  }
  return searchString.includes(FIELD_SEPARATOR);
};

// Maps a job-display-name format token to the operation field it resolves to. Mirrors the
// forward formatter's aliases in lib/utils/jobDisplayName.js. Tokens absent here (e.g.
// {description}) are matched loosely but not extracted.
// Null-prototype so format tokens like {constructor} or {__proto__} can't resolve to
// inherited Object.prototype members and masquerade as mapped fields.
const DISPLAY_NAME_TOKEN_TO_FIELD = Object.assign(Object.create(null), {
  name: WORK_ORDER_OPERATION_FIELDS.WORK_ORDER_ID,
  job: WORK_ORDER_OPERATION_FIELDS.WORK_ORDER_ID,
  workorder: WORK_ORDER_OPERATION_FIELDS.WORK_ORDER_ID,
  wo: WORK_ORDER_OPERATION_FIELDS.WORK_ORDER_ID,
  op: WORK_ORDER_OPERATION_FIELDS.SEQUENCE_NUMBER,
  operation: WORK_ORDER_OPERATION_FIELDS.SEQUENCE_NUMBER,
  sub: WORK_ORDER_OPERATION_FIELDS.SUB,
  lot: WORK_ORDER_OPERATION_FIELDS.LOT,
  split: WORK_ORDER_OPERATION_FIELDS.SPLIT,
  part: 'partNumber',
});

const escapeRegExp = (str) => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

// Regex fragment matching any (possibly empty) PREFIX of a literal string, e.g. " OP" ->
// "(?: (?:O(?:P)?)?)?". Lets a search that stops mid-literal ("1234-2 OP") still anchor at $
// without the preceding capture swallowing the half-typed literal.
const literalPrefixRegex = (literal) => {
  let out = '';
  for (let i = literal.length - 1; i >= 0; i -= 1) {
    out = `(?:${escapeRegExp(literal[i])}${out})?`;
  }
  return out;
};

// Regex fragment matching any (possibly empty) SUFFIX of a literal string, so a search that
// starts mid-literal ("OP10" starting inside " OP") can still anchor at ^.
const literalSuffixRegex = (literal) => {
  let out = '';
  for (let i = 0; i < literal.length; i += 1) {
    out = `(?:${out}${escapeRegExp(literal[i])})?`;
  }
  return out;
};

// Tokenize a format template into alternating literal/token parts.
const tokenizeFormat = (format) => {
  const parts = [];
  let lastIndex = 0;
  const tokenRe = /\{(\w+)\}/g;
  let token = tokenRe.exec(format);
  while (token !== null) {
    if (token.index > lastIndex) {
      parts.push({ literal: format.slice(lastIndex, token.index) });
    }
    parts.push({ token: token[1].toLowerCase() });
    lastIndex = token.index + token[0].length;
    token = tokenRe.exec(format);
  }
  if (lastIndex < format.length) {
    parts.push({ literal: format.slice(lastIndex) });
  }
  return parts;
};

// Fields whose where-clause supports embedded ilike wildcards (sub/lot/split are matched
// verbatim by buildGraphQLWhereClause's caseInsensitive mode, so % markers pass through).
const WILDCARDABLE = new Set([
  WORK_ORDER_OPERATION_FIELDS.SUB,
  WORK_ORDER_OPERATION_FIELDS.LOT,
  WORK_ORDER_OPERATION_FIELDS.SPLIT,
]);

// Cap the number of candidate interpretations so the OR clause stays bounded.
const MAX_CANDIDATES = 24;

// Build an anchored matcher for the WINDOW of template parts [start..end]. A search may be
// ANY substring of the rendered name, so the window can start/end at any token, begin
// partway through the preceding literal, and end partway through the following literal.
// `greedyFirst` controls whether the first captured token matches greedily — values often
// contain the template's own separator ("JOB-1234" vs the "-" in "{name}-{sub}"), so both
// split interpretations are tried and the DB decides which exists. `requireLead`/
// `requireTail` force at least one adjacent-literal character to match, which anchors
// single-token windows (e.g. "OP10") that would otherwise be a bare column search.
const buildWindowMatcher = ({
  parts,
  start,
  end,
  greedyFirst,
  requireLead,
  requireTail,
}) => {
  const leadLiteral =
    start > 0 && parts[start - 1].literal !== undefined
      ? parts[start - 1].literal
      : '';
  const tailLiteral =
    end + 1 < parts.length && parts[end + 1].literal !== undefined
      ? parts[end + 1].literal
      : '';
  if ((requireLead && !leadLiteral) || (requireTail && !tailLiteral)) {
    return null;
  }

  const capturedFields = [];
  let totalTokens = 0;
  let firstCaptureIndex = -1;
  let lastCaptureIndex = -1;
  let regexStr = '^';

  if (leadLiteral) {
    regexStr += requireLead
      ? literalSuffixRegex(leadLiteral.slice(0, -1)) +
        escapeRegExp(leadLiteral[leadLiteral.length - 1])
      : literalSuffixRegex(leadLiteral);
  }

  for (let i = start; i <= end; i += 1) {
    const part = parts[i];
    if (part.literal !== undefined) {
      regexStr += escapeRegExp(part.literal);
    } else {
      totalTokens += 1;
      const greedy = greedyFirst && totalTokens === 1;
      const field = DISPLAY_NAME_TOKEN_TO_FIELD[part.token];
      if (field) {
        if (firstCaptureIndex === -1) {
          firstCaptureIndex = capturedFields.length;
        }
        lastCaptureIndex = capturedFields.length;
        capturedFields.push(field);
        regexStr += greedy ? '(.+)' : '(.+?)';
      } else {
        // Supported-but-unsearchable token (e.g. {description}) — match loosely, no capture.
        regexStr += greedy ? '.+' : '.*?';
      }
    }
  }

  if (tailLiteral) {
    regexStr += requireTail
      ? escapeRegExp(tailLiteral[0]) + literalPrefixRegex(tailLiteral.slice(1))
      : literalPrefixRegex(tailLiteral);
  }
  regexStr += '$';

  return {
    regexStr,
    capturedFields,
    totalTokens,
    firstCaptureIndex,
    lastCaptureIndex,
  };
};

// All field-set interpretations of `search` against one format: every token window of the
// template x greedy/lazy first capture, tolerant of truncated values and partial literals
// on both ends — i.e. the search may be ANY substring of a rendered display name.
// A candidate must be SELECTIVE: either multiple fields, or one field whose value has at
// least two significant characters (wildcards excluded). Without this, a mis-parsed window
// can degrade to something like partNumber contains "A" and flood the results.
const isSelective = (fields) => {
  const keys = Object.keys(fields);
  if (keys.length === 0) {
    return false;
  }
  if (keys.length >= 2) {
    return true;
  }
  return (
    String(fields[keys[0]])
      .replace(/%/g, '')
      .trim().length >= 2
  );
};

const parseOneFormat = (search, format, seen, results) => {
  const push = (fields) => {
    if (!isSelective(fields) || results.length >= MAX_CANDIDATES) {
      return;
    }
    const key = JSON.stringify(fields);
    if (!seen.has(key)) {
      seen.add(key);
      results.push(fields);
    }
  };

  const parts = tokenizeFormat(format);
  const tokenIndexes = [];
  parts.forEach((part, index) => {
    if (part.token !== undefined) {
      tokenIndexes.push(index);
    }
  });

  // Every contiguous token window, widest first so the most specific parses win the cap.
  const windows = [];
  tokenIndexes.forEach((startIdx, i) => {
    tokenIndexes.slice(i).forEach((endIdx, j) => {
      windows.push([startIdx, endIdx, j + 1]);
    });
  });
  windows.sort((a, b) => {
    return b[1] - b[0] - (a[1] - a[0]);
  });

  windows.forEach(([start, end, tokenCount]) => {
    // Single-token windows are a bare column search (the plain OR already covers that)
    // UNLESS at least one adjacent-literal character anchors them (e.g. "OP10").
    const variants = [];
    [false, true].forEach((greedyFirst) => {
      if (tokenCount > 1) {
        variants.push({ greedyFirst, requireLead: false, requireTail: false });
      } else {
        variants.push({ greedyFirst, requireLead: true, requireTail: false });
        variants.push({ greedyFirst, requireLead: false, requireTail: true });
      }
    });

    variants.forEach((variant) => {
      const matcher = buildWindowMatcher({ parts, start, end, ...variant });
      if (!matcher || matcher.capturedFields.length === 0) {
        return;
      }

      let matched;
      try {
        matched = new RegExp(matcher.regexStr, 'i').exec(search.trim());
      } catch (err) {
        return;
      }
      if (!matched) {
        return;
      }

      // Truncation semantics: the first captured value may be cut on the left (match as
      // contains), the last may be cut on the right (match as prefix); middles are exact.
      const fields = {};
      let truncatedSequence = false;
      let invalidInterpretation = false;
      const previousValues = {};
      matcher.capturedFields.forEach((field, index) => {
        // Escape ilike wildcards in the CAPTURED value so a typed % or _ matches literally;
        // the parser then adds its own % markers for truncation semantics.
        const value = (matched[index + 1] || '')
          .trim()
          .replace(/[\\%_]/g, '\\$&');
        if (!value) {
          return;
        }
        const isFirst = index === matcher.firstCaptureIndex;
        const isLast = index === matcher.lastCaptureIndex;
        if (field === WORK_ORDER_OPERATION_FIELDS.SEQUENCE_NUMBER) {
          // A rendered operation number is purely digits. A window whose op capture holds
          // anything else ("1234-2 OP10" swallowed by {op} in the card template) is a WRONG
          // interpretation of the search — discard it entirely rather than letting parseInt
          // or the relaxer degrade it into an overly-broad candidate.
          if (!/^\d+$/.test(value)) {
            invalidInterpretation = true;
            return;
          }
          fields[field] = value;
          if (isFirst || isLast) {
            truncatedSequence = true;
          }
        } else if (WILDCARDABLE.has(field) && isFirst) {
          fields[field] = `%${value}%`;
        } else if (WILDCARDABLE.has(field) && isLast) {
          fields[field] = `${value}%`;
        } else {
          // workOrderId/partNumber are contains-matched by the where builder already.
          fields[field] = value;
        }
        // Two captures mapping to the SAME field with different values (e.g. {workorder}
        // and {name} both -> workOrderId) mean this window sliced the search wrongly —
        // discard rather than let the later capture silently overwrite the earlier one.
        if (
          previousValues[field] !== undefined &&
          previousValues[field] !== fields[field]
        ) {
          invalidInterpretation = true;
        }
        previousValues[field] = fields[field];
      });
      if (invalidInterpretation) {
        return;
      }

      push(fields);
      // A possibly-truncated sequence ("OP1" on the way to "OP10") also gets a variant that
      // matches the digits as a NUMERIC PREFIX (1 -> 1, 10-19, 100-199, ...) so mid-typing
      // never blanks the results — without matching sibling ops the way dropping the filter
      // entirely would (a complete "OP10" must not match OP20).
      if (truncatedSequence) {
        const relaxed = { ...fields };
        relaxed.sequenceNumberPrefix =
          relaxed[WORK_ORDER_OPERATION_FIELDS.SEQUENCE_NUMBER];
        delete relaxed[WORK_ORDER_OPERATION_FIELDS.SEQUENCE_NUMBER];
        push(relaxed);
      }
    });
  });
};

/**
 * Reverse a composed job-display-name search back into candidate operation field-sets.
 *
 * A format (e.g. "{name}-{sub} OP{op}") is turned into anchored regexes with one capture
 * group per mappable token; the search string is matched against them and each captured
 * value assigned to its field. This lets the server-filtered job groups match the composed
 * name an operator sees on a card, not just individual columns.
 *
 * The search may be ANY substring of what the operator sees on a card — including the FULL
 * CARD TITLE "workOrderId | displayName | partNumber" (so "1234-2 OP10 | AVL" works), not
 * just the display-name segment. Values can also contain the template's own separators
 * (work order "JOB-1234" vs the "-" in "{name}-{sub}") which makes the split ambiguous — so
 * this returns EVERY plausible interpretation (all token windows, greedy and lazy splits,
 * truncated edge values, the company format plus the formatter's default fallbacks, each
 * also wrapped in the card-title template). Callers should OR the candidates in the query so
 * the database decides which one actually exists.
 *
 * @param {string} search - the raw search text the operator typed
 * @param {string} format - the company job_display_name_format (may be empty/invalid)
 * @returns {Object[]} candidate parsedFields objects (possibly empty)
 */
// Rendered card titles are short; beyond this the input is a paste/mis-scan, and the
// stacked-capture window regexes backtrack super-linearly on long non-matching strings
// (hundreds of ms on the UI thread at a few KB). Bail out instead.
const MAX_SEARCH_LENGTH = 128;

export const parseDisplayNameSearch = (search, format) => {
  if (
    !search ||
    typeof search !== 'string' ||
    search.trim().length > MAX_SEARCH_LENGTH
  ) {
    return [];
  }

  // The formatter only uses DEFAULT_FORMATS when the company format is empty/invalid, so
  // only parse them in that case — otherwise a default-shaped search could match cards whose
  // rendered name looks nothing like it.
  const baseFormats = (validateJobDisplayNameFormat(format)
    ? [format]
    : DEFAULT_FORMATS
  ).filter((candidate) => {
    return validateJobDisplayNameFormat(candidate);
  });

  // Search-mode job cards render "workOrderId | displayName" (JobCardTitle), and operators
  // copy/type substrings of THAT string — so parse each base format wrapped in the actual
  // card-title template too. (Same-field collisions, e.g. {workorder} vs a {name} token with
  // a different value, invalidate the interpretation.)
  const templates = [];
  baseFormats.forEach((base) => {
    templates.push(base);
    templates.push(`{workorder} | ${base}`);
  });

  const seen = new Set();
  const results = [];
  templates.forEach((template) => {
    parseOneFormat(search, template, seen, results);
  });
  return results;
};
